import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { PersonCard } from '@atlassian/people-teams-ui/directory';
import { useActivityClient } from '@atlassian/recent-work-client/use-activity-client';
import { UserInlinePreview, UserInlinePreviewList } from '@atlassian/user-inline-preview';
import { ErrorCard } from '@atlassian/user-profile-preview/error';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { NLPSearchErrorState } from '../../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../../ai-answer-dialog/nlp-search-result-format';
import { onAIAnswerNotShown } from '../../ai-answer-dialog/on-ai-answer-not-shown';
import type {
	NLPSearchPersonResultDataType,
	NLPSearchWhoResultDataType,
} from '../../ai-answer-dialog/types';
import { getTypedJSONResultData } from '../../ai-answer-dialog/utils/get-typed-json-result-data';
import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { PeopleTeamsAnalyticsListener } from '../../common/controller/analytics/people-teams-analytics-listener';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { logger } from '../../common/utils/logger';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { AnswerCardType } from '../../constants';
import { KnowledgeCarousel } from '../../knowledge-card-components/knowledge-carousel';
import { AIDisclaimerAndFeedback } from '../../v2/common/ui/ai-disclaimer-and-feedback';
import { onKnowledgeCardLinkClick } from '../analytics';
import { fetchPrimaryUserData } from '../gql/hydrate-people-card/utils';
import { KnowledgeCardLoading } from '../knowledge-card-loading';
import { KnowledgeCardsIntentType } from '../knowledge-cards-intent-type';
import { onKnowledgeCardShown } from '../on-knowledge-card-shown';
import { onPersonKnowledgeCardErrored } from '../on-person-knowledge-card-errored';
import { onPersonKnowledgeCardLoaded } from '../on-person-knowledge-card-loaded';
import { SeparatorLineRefresh } from '../separator-line';
import { formatTimeKnowledgeCard } from '../util';
import { PersonCardBody } from './person-card-body';
import { PersonCardHeader } from './person-card-header';
import { SinglePersonKnowledgeCard } from './single-person-knowledge-card';

const CardContainerStyles = cssMap({
	refresh: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	disclaimer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const PersonCardWrapperStyles = cssMap({
	default: {
		width: '180px',
		textOverflow: 'ellipsis',
		whiteSpace: 'normal',
	},
	flex: {
		flexBasis: '180px',
		flexGrow: 0,
		flexShrink: 0,
	},
	inlineWrapper: {
		width: '100%',
		cursor: 'pointer',
	},
});

const USERS_SCROLL_OFFSET = 392;

const messages = defineMessages({
	multiple_people_matches_header: {
		id: 'search_ai.knowledge_cards.multiple_people_matches_header',
		defaultMessage: 'People matches',
		description: 'Header for the multiple people carousel',
	},
	ai_answer_component_topics_not_relevant_checkbox_label: {
		id: 'search_ai.ai_answer_component_topics_not_relevant_checkbox_label',
		defaultMessage: 'The topics are not relevant to this person.',
		description:
			'Label for the checkbox asking if related topics shown for the person are relevant',
	},
});

type IndividualPersonKnowledgeCardProps = {
	primaryUser: NLPSearchWhoResultDataType | undefined;
};

const IndividualPersonKnowledgeCard = ({ primaryUser }: IndividualPersonKnowledgeCardProps) => {
	if (!primaryUser) {
		return null;
	}

	const showKnowledgeCardBody =
		(primaryUser?.recentActivity?.length || 0) > 0 || (primaryUser?.collaborators?.length || 0) > 0;

	return (
		<>
			<Stack xcss={CardContainerStyles.refresh} testId="individual-person-knowledge-card">
				<PersonCardHeader
					accountId={primaryUser.account_id}
					name={primaryUser.name}
					email={primaryUser.email}
					avatarUrl={primaryUser.avatar_url}
					profileUrl={primaryUser.profile_url}
					jobTitle={primaryUser.job_title}
					department={primaryUser.department}
					location={primaryUser.location}
					time={primaryUser.time}
				/>

				{showKnowledgeCardBody && <SeparatorLineRefresh />}
				<Box>
					{showKnowledgeCardBody && (
						<PersonCardBody
							currentUserId={primaryUser.account_id ?? ''}
							recommendedUsers={primaryUser.collaborators || []}
							recentActivity={primaryUser.recentActivity || []}
							profileUrl={primaryUser.profile_url || ''}
						/>
					)}
					<Box xcss={CardContainerStyles.disclaimer}>
						<AIDisclaimerAndFeedback showFeedbackButtons />
					</Box>
				</Box>
			</Stack>
		</>
	);
};

export const PersonKnowledgeCard = ({
	onFMPReached,
}: {
	onFMPReached?: () => void;
}): React.JSX.Element | null => {
	const {
		commonAttributes,
		userDetails: { id: requestingUserId },
		setData,
		sourceProduct,
		loading,
		traceHeaders,
	} = useAIAnswerContext();
	const { formatMessage, locale } = useIntl();
	const activityClient = useActivityClient('v3', 'people');
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [selectedUser, setSelectedUser] = useState<number | undefined>();
	const [primaryUser, setPrimaryUser] = useState<NLPSearchWhoResultDataType | undefined>();
	const { answerString, cloudId, orgId, workspaceId } = commonAttributes;
	const [isPersonCardLoading, setIsPersonCardLoading] = useState(false);
	const shouldRenderPTPersonUX = SAINFeatureControl.isPeopleConnectorUXEnabled();

	const userResults: NLPSearchPersonResultDataType[] = getTypedJSONResultData({ answerString })
		.parsedResult as NLPSearchPersonResultDataType[];

	const getHandleSelectUser = useCallback(
		(index: number) => () => {
			setSelectedUser(index);
			if (fg('kd_fix_sain_mau_instrumentation')) {
				fireSAINUsageEvent({ metCriteria: SainAction.PERSON_SELECTED, commonAttributes });
			}
		},
		[commonAttributes, fireSAINUsageEvent],
	);

	useEffect(() => {
		if (userResults.length === 1) {
			setSelectedUser(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userResults]);

	useEffect(() => {
		const fetchData = async () => {
			if (!shouldRenderPTPersonUX && selectedUser !== undefined) {
				setIsPersonCardLoading(true);
				return fetchPrimaryUserData({
					accountId: userResults[selectedUser].id,
					tenantId: cloudId,
					requestingUserId: requestingUserId || '',
					activityClient,
					locale,
					workspaceId,
					product: sourceProduct ?? '',
					traceHeaders,
				})
					.then((data) => {
						setPrimaryUser(data);
					})
					.catch((error) => {
						setData({
							errorState: NLPSearchErrorState.NetworkError,
							disclaimer: null,
							format: NLPSearchResultFormat.JSON,
							nlpResults: [],
							uniqueSources: [],
							nlpFollowUpResults: null,
						});
						if (fg('kd_search_ai_robust_sentry_logs') || fg('kd_enable_networkerror_logging')) {
							logger.error({
								feature: 'person-knowledge-card',
								element: 'container',
								operation: 'fetch',
								category: 'unknown',
								error,
								functionName: 'fetchPrimaryUserData: PersonKnowledgeCard',
							});
						}
					})
					.finally(() => {
						setIsPersonCardLoading(false);
					});
			}
		};
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser, shouldRenderPTPersonUX]);

	useEffect(() => {
		onFMPReached?.();
		fireAnalyticsEvent(
			onKnowledgeCardShown({
				...commonAttributes,
				cardType: KnowledgeCardsIntentType.PERSON,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fire a one-shot `aiAnswerPersonKnowledgeCard loaded` track event when the multi-person
	// `UserInlinePreviewList` variant is rendered. `success` reflects whether non-empty rows
	// were returned. Mirrors the `singlePerson` variant event fired from
	// `SinglePersonKnowledgeCardRelay`, and the `userInlinePreview` operational request events
	// fired by `@atlassian/user-inline-preview` itself.
	const hasFiredMultiPersonLoadedEventRef = useRef(false);
	useEffect(() => {
		if (!shouldRenderPTPersonUX) {
			return;
		}
		// Single-person branch fires its own event from `SinglePersonKnowledgeCardRelay`.
		if (selectedUser !== undefined || userResults.length <= 1) {
			return;
		}
		if (hasFiredMultiPersonLoadedEventRef.current) {
			return;
		}
		hasFiredMultiPersonLoadedEventRef.current = true;
		// The multi-person list only renders when `userResults.length > 1`, so reaching here
		// always means a successful load — fire `success: true`.
		fireAnalyticsEvent(
			onPersonKnowledgeCardLoaded({
				...commonAttributes,
				personCardVariant: 'multiPerson',
			}),
		);
	}, [shouldRenderPTPersonUX, selectedUser, userResults, fireAnalyticsEvent, commonAttributes]);

	// Handle recent work link clicks from the LinkList component inside UserProfilePreview.
	// Fires `aiAnswerDialog clicked` with `location: 'recent-work-item'` and `answerCardType: 'people'`
	// to match the old person card instrumentation and feed SSRv5 `aiPeopleCardClicks`.
	const handleRecentWorkLinkClick = useCallback(
		(url: string, index: number, opts?: { isAuxClick?: boolean; isContextMenu?: boolean }) => {
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: 'recent-work-item',
					position: index,
					answerCardType: AnswerCardType.PERSON,
					isAuxClick: opts?.isAuxClick,
					isContextMenu: opts?.isContextMenu,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED, commonAttributes });
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	const handleProfileError = useCallback<
		NonNullable<React.ComponentProps<typeof ReactErrorBoundary>['onError']>
	>(
		(error, errorInfo) => {
			fireAnalyticsEvent(
				onPersonKnowledgeCardErrored({
					...commonAttributes,
					personCardVariant: 'singlePerson',
					error,
					errorInfo,
				}),
			);
			fireAnalyticsEvent(
				onAIAnswerNotShown({
					...commonAttributes,
					isStreamed: false,
					errorState: NLPSearchErrorState.RenderError,
					error,
					errorInfo,
				}),
			);
		},
		[fireAnalyticsEvent, commonAttributes],
	);

	if (loading || (!shouldRenderPTPersonUX && isPersonCardLoading)) {
		return <KnowledgeCardLoading />;
	}

	if (userResults.length === 0) {
		return null;
	}

	if (selectedUser !== undefined || userResults.length === 1) {
		if (shouldRenderPTPersonUX) {
			const index = selectedUser ?? 0;
			const accountId = userResults[index]?.id;
			if (!accountId) {
				return null;
			}
			const profile = (
				<SinglePersonKnowledgeCard
					accountId={accountId}
					cloudId={cloudId}
					orgId={orgId}
					requestingUserId={requestingUserId || ''}
					sourceProduct={sourceProduct}
					onLinkClick={handleRecentWorkLinkClick}
					onError={handleProfileError}
				>
					<AIDisclaimerAndFeedback showFeedbackButtons />
				</SinglePersonKnowledgeCard>
			);
			return (
				<PeopleTeamsAnalyticsListener>
					<Stack space="space.150">
						<ReactErrorBoundary onError={handleProfileError} fallback={<ErrorCard />}>
							{profile}
						</ReactErrorBoundary>
					</Stack>
				</PeopleTeamsAnalyticsListener>
			);
		}
		return <IndividualPersonKnowledgeCard primaryUser={primaryUser} />;
	}

	return (
		<>
			<Stack space="space.150">
				{shouldRenderPTPersonUX ? (
					<PeopleTeamsAnalyticsListener>
						<Stack space="space.100">
							<Text size="large" weight="semibold">
								{formatMessage(messages.multiple_people_matches_header)}
							</Text>
							<UserInlinePreviewList layout="grid" testId="person-knowledge-card-user-inline-list">
								{userResults.map((user, index) => (
									// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
									<Box
										key={user.id}
										onClick={getHandleSelectUser(index)}
										xcss={PersonCardWrapperStyles.inlineWrapper}
										testId={`person-knowledge-card-wrapper-${user.id}`}
									>
										<UserInlinePreview
											data={{
												accountId: user.id,
												name: user.name,
												avatarUrl: user.avatarUrl ?? null,
												jobTitle: user.title ?? null,
											}}
											cloudId={cloudId}
											requestingUserId={requestingUserId || undefined}
											testId={`person-knowledge-card-user-inline-${user.id}`}
										/>
									</Box>
								))}
							</UserInlinePreviewList>
						</Stack>
					</PeopleTeamsAnalyticsListener>
				) : (
					<KnowledgeCarousel
						heading={formatMessage(messages.multiple_people_matches_header)}
						scrollOffset={USERS_SCROLL_OFFSET}
					>
						{userResults.map((user, index) => (
							// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
							<Box
								onClick={getHandleSelectUser(index)}
								xcss={cx(PersonCardWrapperStyles.default, PersonCardWrapperStyles.flex)}
								testId="person-knowledge-card-wrapper"
								key={user.id}
							>
								<PersonCard
									data={{
										id: user.id,
										name: user.name,
										jobTitle: user.title ?? '',
										avatarUrl: user.avatarUrl,
										localTime: formatTimeKnowledgeCard({ locale, zoneInfo: user.zoneInfo }),
										location: user.location ?? '',
									}}
								/>
							</Box>
						))}
					</KnowledgeCarousel>
				)}
				<AIDisclaimerAndFeedback showFeedbackButtons />
			</Stack>
		</>
	);
};
